import { usePlaybackPlay } from '@app/core/playback/hooks/usePlayback';
import { usePostHogCaptureLiveTopics } from '@app/modules/logger/screens/stream/features/live-topics/hooks/usePostHogCaptureLiveTopics';
import { useStreamUserDefinedRange } from '@app/modules/logger/screens/stream/stores/stream-screen.store';
import { nlpIntegration } from '@mi-front/api-clients';
import { minsToMs } from '@mi-front/core';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { useGoToTime } from '../../../../hooks/cursor/useGoToTime';

interface UseLiveTopicNavigationParams {
  topic: nlpIntegration.TopicOutDto;
}

/**
 * Encapsulates "go to" and "play" actions for a live topic detail item.
 * Avoids auto-playing when a go-to requires loading a different stream range.
 */
export function useLiveTopicNavigation({ topic }: UseLiveTopicNavigationParams) {
  const capture = usePostHogCaptureLiveTopics();
  const { goToTime } = useGoToTime({ focusOffsetMs: minsToMs(2) });
  const play = usePlaybackPlay();
  const userRange = useStreamUserDefinedRange();

  const topicStartTs = DateTime.fromISO(topic.start_time).toMillis();
  const isTopicInLoadedRange =
    !userRange.isLoading && topicStartTs >= userRange.from && topicStartTs <= userRange.to;

  const handleGoTo = useCallback(() => {
    capture('go_to_time_clicked', { topic_id: topic.id, source: 'panel_detail' });
    goToTime(topicStartTs);

    if (!isTopicInLoadedRange) {
      return;
    }

    play?.({
      startTs: topicStartTs,
      playAfterSeek: false,
    });
  }, [topicStartTs, topic.id, goToTime, play, capture, isTopicInLoadedRange]);

  const handlePlay = useCallback(() => {
    capture('play_from_topic_clicked', { topic_id: topic.id, source: 'panel_detail' });
    goToTime(topicStartTs);
    play?.({
      startTs: topicStartTs,
    });
  }, [topic.id, goToTime, play, capture, topicStartTs]);

  return {
    handleGoTo,
    handlePlay: isTopicInLoadedRange ? handlePlay : null,
  };
}
